import {
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  effect,
  inject,
  input,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PopoverModule, Popover } from 'primeng/popover';
import { DialogModule } from 'primeng/dialog';
import { AvatarModule } from 'primeng/avatar';
import { SelectModule } from 'primeng/select';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { PreferencesService, AuthService, MessengerUnreadService } from '../../core/services';
import { User } from '../../models/user';
import { BalanceStore } from '../../core/stores';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE, Language } from '../../core/constants';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  HelpCenterComponent,
  BalanceHistoryComponent,
  AppsDialogComponent,
} from '../../features/dialogs';
import { UserPopoverComponent } from '../../features/others/user-popover/user-popover.component';
import { environment } from '../../../environments/environment';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    PopoverModule,
    DialogModule,
    AvatarModule,
    SelectModule,
    DynamicDialogModule,
    TranslocoPipe,
    UserPopoverComponent,
    TooltipModule,
  ],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  providers: [DialogService],
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild('userPopover') userPopover!: Popover;

  isDark: any = false;
  currentUser: User | null = null;
  balanceStore = inject(BalanceStore);
  preferencesService = inject(PreferencesService);
  authService = inject(AuthService);
  translocoService = inject(TranslocoService);
  dialogService = inject(DialogService);
  router = inject(Router);
  messengerUnreadService = inject(MessengerUnreadService);

  private scrollSubscription?: Subscription;

  availableLanguages = AVAILABLE_LANGUAGES;
  currentLanguage: Language =
    AVAILABLE_LANGUAGES.find((lang) => lang.code === DEFAULT_LANGUAGE) || AVAILABLE_LANGUAGES[0];

  // App version info
  appVersion = environment.version;
  appName = environment.appName;
  isAiAgentRoute = input<boolean>(false);
  activeMenuItem = 'smart-org';

  navItems = [
    {
      route: 'ai-agent',
      labelKey: 'navbar.aiChat',
      iconClass: 'fa-light fa-robot text-xl',
      disable: false,
    },
    {
      route: 'messenger',
      labelKey: 'navbar.orgChat',
      iconClass: 'fa-light fa-messages text-xl',
      disable: false,
    },
    {
      route: 'calendar',
      labelKey: 'navbar.calendar',
      iconClass: 'fa-light fa-calendar text-xl',
      disable: true,
    },
    {
      route: 'planner',
      labelKey: 'navbar.planner',
      iconClass: 'fa-light fa-list-check text-xl',
      disable: true,
    },
    {
      route: 'jobs',
      labelKey: 'navbar.jobs',
      iconClass: 'fa-light fa-briefcase text-xl',
      disable: true,
    },
  ];

  // Get unread counts from service
  get messengerUnreadMessages(): number {
    return this.messengerUnreadService.unreadMessages();
  }

  get messengerUnreadMentions(): number {
    return this.messengerUnreadService.unreadMentions();
  }

  get shouldShowMessengerBadge(): boolean {
    return this.messengerUnreadService.shouldShowBadge();
  }

  constructor() {
    effect(() => {
      this.currentUser = this.authService.currentUser?.user || null;
    });
  }

  get currentUserBalance(): string {
    return this.balanceStore.balance.formatted();
  }

  get balanceLoading(): boolean {
    return this.balanceStore.balance.loading();
  }

  async ngOnInit() {
    // Balance is loaded from app component, no need to load here
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateActiveNavItem();
    });

    this.updateActiveNavItem();
    this.checkTheme();
    this.initializeLanguage();
  }

  ngOnDestroy() {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  updateActiveNavItem() {
    this.activeMenuItem = this.router.url;
  }

  private checkTheme() {
    this.isDark = this.preferencesService.darkMode;
    if (this.isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  navigateToRoute(route: string) {
    this.router.navigate([route]);
    this.activeMenuItem = route;
  }

  openAppsDialog() {
    this.dialogService.open(AppsDialogComponent, {
      header: this.translocoService.translate('navbar.availableApps'),
      width: '500px',
      styleClass: 'apps-dialog',
      modal: true,
      dismissableMask: true,
      closable: true,
      showHeader: false,
    });
  }

  toggleTheme() {
    this.preferencesService.toggleDarkMode();
    this.isDark = this.preferencesService.darkMode;
    if (this.isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  toggleUserPopover(event: any) {
    this.userPopover.toggle(event);
  }

  onLogoutClick() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  goToProfile() {
    this.userPopover.hide();
    this.router.navigate(['/profile']);
  }

  goToBalanceHistory() {
    this.dialogService.open(BalanceHistoryComponent, {
      header: this.translocoService.translate('header.user.balanceHistory'),
      width: '100vw',
      styleClass: 'balance-history-dialog',
      modal: true,
      maximizable: true,
      dismissableMask: true,
      closable: true,
    });
    this.userPopover.hide();
  }

  goToHelpCenter() {
    this.dialogService.open(HelpCenterComponent, {
      header: this.translocoService.translate('header.user.helpCenter'),
      width: '60vw',
      styleClass: 'help-center-dialog',
      modal: true,
      dismissableMask: true,
      closable: true,
    });
    this.userPopover.hide();
  }

  goToSettings() {
    this.userPopover.hide();
    this.router.navigate(['/settings']);
  }

  logout() {
    this.authService.logout();
    this.userPopover.hide();
    this.router.navigate(['/login']);
  }

  private initializeLanguage() {
    const savedLanguage = localStorage.getItem('selectedLanguage') || DEFAULT_LANGUAGE;
    const language = this.availableLanguages.find((lang) => lang.code === savedLanguage);
    if (language) {
      this.currentLanguage = language;
      this.translocoService.setActiveLang(language.code);
    }
  }

  onLanguageSelect(language: Language) {
    this.currentLanguage = language;
    this.translocoService.setActiveLang(language.code);
    localStorage.setItem('selectedLanguage', language.code);

    const direction =
      this.currentLanguage.code === 'fa' || this.currentLanguage.code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = direction;
    document.documentElement.lang = this.currentLanguage.code;
  }

  selectLanguage(language: Language) {
    this.userPopover.hide();
    this.currentLanguage = language;
    this.translocoService.setActiveLang(language.code);
    localStorage.setItem('selectedLanguage', language.code);

    const direction =
      this.currentLanguage.code === 'fa' || this.currentLanguage.code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = direction;
    document.documentElement.lang = this.currentLanguage.code;
  }

  getMessengerTooltip(): string {
    const orgChatLabel = this.translocoService.translate('navbar.orgChat');
    const unreadLabel = this.translocoService.translate('navbar.unread');
    return `<div style="font-weight: 600; margin-bottom: 4px;">${orgChatLabel}</div>
      <div style="font-size: 0.875rem;" class="text-center">${this.messengerUnreadMessages} ${unreadLabel} </div>
      <div style="font-size: 0.875rem;" class="text-center text-rose-400/80">${this.messengerUnreadMentions} @</div>`;
  }
}
